// src/scripts/useScripts/useUserRole.js
import { ref, computed } from "vue";
import { doc, getDoc } from "firebase/firestore";
import { db } from "@/firebase";

const role = ref(null);
const isAdmin = ref(false);
const roleLoaded = ref(false);

// Optional helpers for UI
const userType = ref("single"); // single | multi | operations
const isOperations = ref(false);
const venueIds = ref([]);       // array of assigned venues (admin + multi)
const primaryVenueId = ref(""); // venue_id

// ✅ CHANGE: NEW TAGS (extra only)
const managerTag = ref(false);
const staffTag = ref(false);

// ✅ CHANGE: derived UI booleans
const isManager = computed(() => !isOperations.value && (isAdmin.value || managerTag.value));
const isStaff = computed(() => staffTag.value === true);

function asBool(v) {
  if (v === true) return true;
  if (v === false) return false;
  if (typeof v === "string") {
    const s = v.trim().toLowerCase();
    if (s === "true") return true;
    if (s === "false") return false;
  }
  return false;
}

function asString(v) {
  return String(v ?? "").trim();
}

function uniqStrings(arr) {
  const out = [];
  const seen = new Set();
  for (const v of Array.isArray(arr) ? arr : []) {
    const s = asString(v);
    if (!s) continue;
    if (seen.has(s)) continue;
    seen.add(s);
    out.push(s);
  }
  return out;
}

// IMPORTANT: support BOTH localStorage keys to avoid regressions
function setActiveVenueToStorage(v) {
  const val = asString(v);
  if (!val) return;
  localStorage.setItem("coredeck_venue", val);
  localStorage.setItem("venue", val);
}

function setMultiVenuesToStorage(ids) {
  localStorage.setItem("multiVenueIds", JSON.stringify(Array.isArray(ids) ? ids : []));
}

function clearMultiVenuesFromStorage() {
  localStorage.removeItem("multiVenueIds");
}

export function useUserRole() {
  async function loadUserRole(uid) {
    roleLoaded.value = false;

    if (!uid) {
      role.value = null;
      isAdmin.value = false;
      userType.value = "single";
      isOperations.value = false;
      venueIds.value = [];
      primaryVenueId.value = "";

      // ✅ CHANGE: reset tags
      managerTag.value = false;
      staffTag.value = false;

      clearMultiVenuesFromStorage();
      roleLoaded.value = true;
      return;
    }

    const snap = await getDoc(doc(db, "users", uid));
    const data = snap.exists() ? (snap.data() || {}) : {};

    const r = asString(data.role || "user");
    role.value = r;
    isAdmin.value = r === "admin";

    // ops flag
    const ops = asBool(data.isOperations ?? data.is_operations ?? data.operations ?? false);
    isOperations.value = ops;

    // venue binding
    const vId = asString(data.venue_id || data.venueId || data.activeVenueId || data.venue || "");
    const vIds = uniqStrings(data.venue_ids || data.venueIds || []);

    primaryVenueId.value = vId;
    venueIds.value = vIds;

    const ut = asString(
      data.user_type ||
      data.userType ||
      (ops ? "operations" : (vIds.length > 1 ? "multi" : "single"))
    );
    userType.value = ut;

    // ✅ CHANGE: load tags from user doc (extra-only)
    // Ops MUST never see staff module, so managerTag forced false if ops.
    managerTag.value = ops ? false : asBool(data.managerTag ?? false);
    staffTag.value = asBool(data.staffTag ?? false);

    // Storage rules:
    // - operations: do not force venue (they choose); keep any existing selection
    // - non-ops: set active venue to their bound venue_id
    if (!ops && vId) setActiveVenueToStorage(vId);

    // multi venue ids for UI dropdown
    if (!ops && vIds.length > 1) {
      setMultiVenuesToStorage(vIds);
    } else {
      clearMultiVenuesFromStorage();
    }

    roleLoaded.value = true;
  }

  return {
    role,
    isAdmin,
    roleLoaded,
    loadUserRole,

    // optional helpers
    userType,
    isOperations,
    venueIds,
    primaryVenueId,

    // ✅ CHANGE: export tags + derived flags
    managerTag,
    staffTag,
    isManager,
    isStaff,
  };
}
